import React, { useState, useEffect } from 'react'
import { 
  Package, Plus, Edit2, Trash2, Save, X, 
  Gift, Star, DollarSign, Eye, EyeOff,
  TrendingUp, Clock, Users
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { REWARD_CATEGORIES, categoriesIn, categoryInfo, categoryKey } from '@/utils/rewardCategories'

interface Reward {
  id: string
  name: string
  cost_points: number
  description?: string
  category?: string
  availability: 'always' | 'limited' | 'seasonal'
  // Limited Quantity: limit_count redemptions per kid per limit_period,
  // and/or a family-wide stock that counts down.
  limit_count?: number | null
  limit_period?: 'day' | 'week' | 'month' | null
  stock_remaining?: number | null
  active: boolean
  requires_parent_approval: boolean
  icon?: string
  user_redemption_count?: number
}

const PERIODS = [
  { value: 'day', label: 'day' },
  { value: 'week', label: 'week' },
  { value: 'month', label: 'month' }
] as const

// The API puts its message in `error`; older endpoints used `detail`.
const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.response?.data?.detail || fallback

// Only the fields the form edits, so a save never writes back list-only
// values (redemption counts). Switching to Always Available clears limits.
const rewardPayload = (r: Partial<Reward>) => {
  const limited = r.availability === 'limited'
  return {
    name: r.name,
    cost_points: r.cost_points,
    description: r.description,
    category: r.category,
    availability: limited ? 'limited' : 'always',
    requires_parent_approval: r.requires_parent_approval,
    active: r.active,
    limit_count: limited && r.limit_count ? r.limit_count : null,
    limit_period: limited && r.limit_count ? r.limit_period || 'week' : null,
    stock_remaining: limited && r.stock_remaining != null ? r.stock_remaining : null
  }
}

const limitLabel = (r: Reward) => {
  const parts: string[] = []
  if (r.limit_count && r.limit_period) parts.push(`Max ${r.limit_count} per kid per ${r.limit_period}`)
  if (r.stock_remaining != null) parts.push(`${r.stock_remaining} in stock`)
  return parts.join(' · ')
}

// Icons for the standard categories; anything else gets the gift icon.
const CATEGORY_STYLE: Record<string, { icon: React.ElementType; color: string }> = {
  privileges: { icon: Star, color: 'bg-purple-500' },
  screen_time: { icon: Clock, color: 'bg-blue-500' },
  treats: { icon: Gift, color: 'bg-pink-500' },
  social: { icon: Users, color: 'bg-green-500' },
  money: { icon: DollarSign, color: 'bg-yellow-500' },
  activities: { icon: TrendingUp, color: 'bg-orange-500' },
  purchases: { icon: Package, color: 'bg-indigo-500' },
}

export const RewardsManager: React.FC = () => {
  const [rewards, setRewards] = useState<Reward[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddReward, setShowAddReward] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'disabled'>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)
  // Points-per-dollar; blank means the store shows no money note.
  const [pointsPerDollar, setPointsPerDollar] = useState('')
  const [savingRate, setSavingRate] = useState(false)
  
  const [newReward, setNewReward] = useState<Partial<Reward>>({
    name: '',
    cost_points: 50,
    description: '',
    category: 'privileges',
    availability: 'always',
    active: true,
    requires_parent_approval: true
  })

  useEffect(() => {
    loadRewards()
    apiClient
      .get('/api/exchange-rate')
      .then((r: any) => setPointsPerDollar(r?.configured ? String(r.points_per_dollar) : ''))
      .catch(() => {})
  }, [])

  const saveExchangeRate = async () => {
    const value = pointsPerDollar.trim()
    if (value !== '' && !(Number(value) > 0)) {
      toast.error('Enter a number of points, or leave it blank')
      return
    }
    setSavingRate(true)
    try {
      await apiClient.put('/api/system/settings', { exchange_rate: value })
      toast.success(value ? `Saved: ${value} points = $1.00` : 'Points-to-money note turned off')
    } catch (error: any) {
      toast.error(apiError(error, 'Failed to save exchange rate'))
    } finally {
      setSavingRate(false)
    }
  }

  const loadRewards = async () => {
    setLoading(true)
    try {
      // Parents get disabled rewards too, for the Show Disabled view.
      const data = await apiClient.get('/api/rewards?include_inactive=true')
      setRewards(data)
    } catch (error) {
      console.error('Failed to load rewards:', error)
      toast.error('Failed to load rewards')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateReward = async () => {
    if (!newReward.name || !newReward.cost_points) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await apiClient.post('/api/rewards', rewardPayload(newReward))
      toast.success('Reward created successfully')
      setShowAddReward(false)
      setNewReward({
        name: '',
        cost_points: 50,
        description: '',
        category: 'privileges',
        availability: 'always',
        active: true,
        requires_parent_approval: true
      })
      loadRewards()
    } catch (error: any) {
      toast.error(apiError(error, 'Failed to create reward'))
    }
  }

  const handleUpdateReward = async () => {
    if (!editingReward) return

    try {
      await apiClient.patch(`/api/rewards/${editingReward.id}`, rewardPayload(editingReward))
      toast.success('Reward updated successfully')
      setEditingReward(null)
      loadRewards()
    } catch (error: any) {
      toast.error(apiError(error, 'Failed to update reward'))
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    if (!confirm('Are you sure you want to delete this reward?')) return
    
    try {
      await apiClient.delete(`/api/rewards/${rewardId}`)
      toast.success('Reward deleted successfully')
      loadRewards()
    } catch (error: any) {
      toast.error(apiError(error, 'Failed to delete reward'))
    }
  }

  const handleToggleActive = async (rewardId: string, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/rewards/${rewardId}`, { active: !isActive })
      toast.success(`Reward ${!isActive ? 'enabled' : 'disabled'}`)
      loadRewards()
    } catch (error: any) {
      toast.error(apiError(error, 'Failed to update reward'))
    }
  }

  // The add and edit forms share one set of fields.
  const formReward: Partial<Reward> = editingReward ?? newReward
  const updateForm = (patch: Partial<Reward>) =>
    editingReward ? setEditingReward({ ...editingReward, ...patch }) : setNewReward({ ...newReward, ...patch })

  const filteredRewards = rewards.filter(reward => {
    const matchesSearch = reward.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          reward.description?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFilter = filter === 'all' ||
                          (filter === 'active' && reward.active) ||
                          (filter === 'disabled' && !reward.active)

    const matchesCategory = categoryFilter === 'all' || categoryKey(reward.category) === categoryFilter

    // Disabled rewards are listed in their own section below (Show Disabled).
    if (!reward.active) return false

    return matchesSearch && matchesFilter && matchesCategory
  })

  const getCategoryInfo = (category: string) => {
    const info = categoryInfo(category)
    return { ...info, ...(CATEGORY_STYLE[info.value] || { icon: Gift, color: 'bg-gray-500' }) }
  }

  const activeRewardsCount = rewards.filter(r => r.active).length
  const disabledRewardsCount = rewards.filter(r => !r.active).length

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Rewards Management</h2>
        <p className="text-gray-600">Create and manage rewards that kids can redeem</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Rewards</p>
              <p className="text-2xl font-bold text-green-600">{activeRewardsCount}</p>
            </div>
            <Gift className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Disabled Rewards</p>
              <p className="text-2xl font-bold text-gray-600">{disabledRewardsCount}</p>
            </div>
            <EyeOff className="w-8 h-8 text-gray-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Redemptions</p>
              <p className="text-2xl font-bold text-blue-600">
                {rewards.reduce((sum, r) => sum + (r.user_redemption_count || 0), 0)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Cost</p>
              <p className="text-2xl font-bold text-purple-600">
                {Math.round(rewards.reduce((sum, r) => sum + r.cost_points, 0) / (rewards.length || 1))}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Points to money (optional) */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Points to money (optional)</h3>
            <p className="text-sm text-gray-600">
              If you pay out points as cash, set how many points make $1.00. Kids see the rate in the Rewards
              Store. Leave it blank to hide it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              value={pointsPerDollar}
              onChange={(e) => setPointsPerDollar(e.target.value)}
              placeholder="e.g. 10"
              className="w-28 px-3 py-2 border rounded-lg"
              aria-label="Points per dollar"
            />
            <span className="text-sm text-gray-600 whitespace-nowrap">points = $1.00</span>
            <button
              onClick={saveExchangeRate}
              disabled={savingRate}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search rewards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Categories</option>
              {categoriesIn(rewards).map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowDisabled(!showDisabled)}
              className={`px-4 py-2 rounded-lg flex items-center ${
                showDisabled ? 'bg-gray-200' : 'bg-gray-100'
              }`}
            >
              {showDisabled ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
              {showDisabled ? 'Hide' : 'Show'} Disabled
            </button>
          </div>
          <button
            onClick={() => setShowAddReward(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Reward
          </button>
        </div>
      </div>

      {/* Rewards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRewards.map(reward => {
          const categoryInfo = getCategoryInfo(reward.category || 'privileges')

          return (
            <div
              key={reward.id}
              className="bg-white rounded-lg shadow p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${categoryInfo.color} bg-opacity-10 mr-3`}>
                    <categoryInfo.icon className={`w-5 h-5 ${categoryInfo.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{reward.name}</h3>
                    <span className="text-xs text-gray-500">{categoryInfo.label}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setEditingReward(reward)}
                    className="p-1 text-blue-600 hover:text-blue-900"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(reward.id, reward.active)}
                    className="p-1 text-yellow-600 hover:text-yellow-900"
                  >
                    {reward.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDeleteReward(reward.id)}
                    className="p-1 text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-blue-600">
                    {reward.cost_points} pts
                  </span>
                  {reward.requires_parent_approval && (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                      Requires Approval
                    </span>
                  )}
                </div>
                {reward.description && (
                  <p className="text-sm text-gray-600">{reward.description}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                {reward.availability === 'limited' && limitLabel(reward) ? (
                  <span>{limitLabel(reward)}</span>
                ) : (
                  <span />
                )}
                <span className={`px-2 py-1 rounded ${
                  reward.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {reward.active ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Disabled Rewards Section */}
      {showDisabled && disabledRewardsCount > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Disabled Rewards ({disabledRewardsCount})
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rewards.filter(r => !r.active).map(reward => (
                <div key={reward.id} className="bg-white rounded p-3 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium">{reward.name}</h4>
                      <p className="text-sm text-gray-500">{reward.cost_points} points</p>
                    </div>
                    <button
                      onClick={() => handleToggleActive(reward.id, false)}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Reward Modal */}
      {(showAddReward || editingReward) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingReward ? 'Edit Reward' : 'Add New Reward'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={editingReward ? editingReward.name : newReward.name}
                  onChange={(e) => editingReward 
                    ? setEditingReward({ ...editingReward, name: e.target.value })
                    : setNewReward({ ...newReward, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Extra Screen Time"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Points *</label>
                  <input
                    type="number"
                    min="1"
                    value={editingReward ? editingReward.cost_points : newReward.cost_points}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, cost_points: parseInt(e.target.value) })
                      : setNewReward({ ...newReward, cost_points: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={categoryKey(formReward.category)}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, category: e.target.value })
                      : setNewReward({ ...newReward, category: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {/* The standard list plus any other category the family uses. */}
                    {categoriesIn([...REWARD_CATEGORIES.map(c => ({ category: c.value })), ...rewards, formReward]).map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editingReward ? editingReward.description || '' : newReward.description}
                  onChange={(e) => editingReward
                    ? setEditingReward({ ...editingReward, description: e.target.value })
                    : setNewReward({ ...newReward, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Describe the reward..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Availability</label>
                <select
                  value={formReward.availability === 'limited' ? 'limited' : 'always'}
                  onChange={(e) => updateForm({ availability: e.target.value as Reward['availability'] })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="always">Always available</option>
                  <option value="limited">Limited quantity</option>
                </select>
              </div>
              {formReward.availability === 'limited' && (
                <div className="space-y-3 rounded-lg bg-gray-50 p-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Limit per kid</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={formReward.limit_count ?? ''}
                        onChange={(e) =>
                          updateForm({ limit_count: e.target.value ? parseInt(e.target.value) : null })
                        }
                        className="w-20 px-3 py-2 border rounded-lg"
                        placeholder="1"
                        aria-label="Redemptions allowed per kid"
                      />
                      <span className="text-sm text-gray-600">per</span>
                      <select
                        value={formReward.limit_period || 'week'}
                        onChange={(e) => updateForm({ limit_period: e.target.value as Reward['limit_period'] })}
                        className="px-3 py-2 border rounded-lg"
                        aria-label="Limit period"
                      >
                        {PERIODS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      How often each kid can redeem it. Leave blank for no per-kid limit.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Stock (optional)</label>
                    <input
                      type="number"
                      min="0"
                      value={formReward.stock_remaining ?? ''}
                      onChange={(e) =>
                        updateForm({ stock_remaining: e.target.value === '' ? null : parseInt(e.target.value) })
                      }
                      className="w-28 px-3 py-2 border rounded-lg"
                      placeholder="No limit"
                      aria-label="Stock remaining"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Total left for the whole family. It counts down with each redemption.
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingReward ? editingReward.requires_parent_approval : newReward.requires_parent_approval}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, requires_parent_approval: e.target.checked })
                      : setNewReward({ ...newReward, requires_parent_approval: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm">Requires parent approval</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingReward ? editingReward.active : newReward.active}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, active: e.target.checked })
                      : setNewReward({ ...newReward, active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm">Active</label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddReward(false)
                  setEditingReward(null)
                  setNewReward({
                    name: '',
                    cost_points: 50,
                    description: '',
                    category: 'privileges',
                    availability: 'always',
                    active: true,
                    requires_parent_approval: true
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={editingReward ? handleUpdateReward : handleCreateReward}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingReward ? 'Save Changes' : 'Create Reward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}